/**
 * usePyodide Hook
 * Provides Pyodide (Python in browser) initialization and execution
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PYODIDE_CONFIG } from '@/constants';
import type { ConsoleEntry, ConsoleOutputType, PowerFlowResults } from '@/types';

// ============================================================
// TYPES
// ============================================================

/** Pyodide lifecycle status */
export type PyodideStatus = 'loading' | 'ready' | 'error';

/** Pyodide interface (simplified) */
export interface PyodideInterface {
  runPython: (code: string) => unknown;
  runPythonAsync: (code: string) => Promise<unknown>;
  loadPackage: (packages: string[]) => Promise<void>;
  pyimport: (module: string) => { install: (pkg: string) => Promise<void> };
  globals: {
    get: (name: string) => string;
    set: (name: string, value: unknown) => void;
  };
}

/** Options for the usePyodide hook */
export interface UsePyodideOptions {
  /** Whether to auto-initialize on mount */
  autoInit?: boolean;
  /** Callback when status changes */
  onStatusChange?: (status: PyodideStatus, message: string) => void;
  /** Callback for console output */
  onConsoleOutput?: (output: ConsoleEntry) => void;
  /** Custom packages to install */
  additionalPackages?: string[];
}

/** Return type for the usePyodide hook */
export interface UsePyodideReturn {
  /** Pyodide instance */
  pyodide: PyodideInterface | null;
  /** Current status */
  status: PyodideStatus;
  /** Status message */
  message: string;
  /** Whether currently running code */
  isRunning: boolean;
  /** Console output history */
  consoleOutput: ConsoleEntry[];
  /** Last initialization error */
  error: Error | null;
  /** Loading progress percentage */
  loadingProgress: number;
  /** Initialize Pyodide */
  initialize: () => Promise<void>;
  /** Run Python code */
  runPython: (code: string) => Promise<unknown>;
  /** Run Python code async */
  runPythonAsync: (code: string) => Promise<unknown>;
  /** Add console output */
  addConsoleOutput: (type: ConsoleOutputType, message: string) => void;
  /** Clear console output */
  clearConsole: () => void;
  /** Check if ready */
  isReady: boolean;
}

// ============================================================
// PYTHON SETUP CODE
// ============================================================

const PYTHON_CAPTURE_SETUP = `
import sys
from io import StringIO

class OutputCapture:
    def __init__(self):
        self.output = []
    
    def write(self, text):
        if text.strip():
            self.output.append(text)
    
    def flush(self):
        pass
    
    def get(self):
        result = self.output.copy()
        self.output = []
        return result

_capture = OutputCapture()
sys.stdout = _capture
sys.stderr = _capture

def get_output():
    return _capture.get()
`;

// ============================================================
// MAIN HOOK
// ============================================================

/**
 * Hook for managing Pyodide (Python in browser) initialization and execution
 * 
 * @param options - Configuration options
 * @returns Pyodide controls and state
 * 
 * @example
 * ```tsx
 * const {
 *   pyodide,
 *   status,
 *   message,
 *   isRunning,
 *   runPythonAsync,
 *   addConsoleOutput,
 *   consoleOutput,
 *   isReady,
 * } = usePyodide();
 * 
 * const runCode = async () => {
 *   if (!isReady) return;
 *   const result = await runPythonAsync('print("Hello from Python!")');
 * };
 * ```
 */
export function usePyodide(options: UsePyodideOptions = {}): UsePyodideReturn {
  const {
    autoInit = true,
    onStatusChange,
    onConsoleOutput,
    additionalPackages = [],
  } = options;

  // State
  const [pyodide, setPyodide] = useState<PyodideInterface | null>(null);
  const [status, setStatus] = useState<PyodideStatus>('loading');
  const [message, setMessage] = useState('Initializing...');
  const [isRunning, setIsRunning] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState<ConsoleEntry[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Refs
  const initializingRef = useRef(false);
  const pyodideRef = useRef<PyodideInterface | null>(null);

  // Add console output
  const addConsoleOutput = useCallback(
    (type: ConsoleOutputType, msg: string) => {
      const output: ConsoleEntry = {
        type,
        message: msg,
        time: new Date().toLocaleTimeString(),
        timestamp: new Date(),
      };
      setConsoleOutput((prev) => [...prev, output]);
      onConsoleOutput?.(output);
    },
    [onConsoleOutput]
  );

  // Clear console
  const clearConsole = useCallback(() => {
    setConsoleOutput([]);
  }, []);

  // Update status with callback
  const updateStatus = useCallback(
    (newStatus: PyodideStatus, newMessage: string) => {
      setStatus(newStatus);
      setMessage(newMessage);
      onStatusChange?.(newStatus, newMessage);
    },
    [onStatusChange]
  );

  // Initialize Pyodide
  const initialize = useCallback(async () => {
    // Prevent double initialization
    if (initializingRef.current || pyodideRef.current) {
      return;
    }
    initializingRef.current = true;

    try {
      setError(null);
      setLoadingProgress(0);
      addConsoleOutput('info', 'ðŸ Loading Python...');
      updateStatus('loading', 'Loading Python...');

      // Load Pyodide
      // @ts-expect-error - loadPyodide is loaded from CDN
      const py = await loadPyodide({
        indexURL: PYODIDE_CONFIG.indexURL,
      });

      addConsoleOutput('info', 'ðŸ“¦ Installing packages...');
      updateStatus('loading', 'Installing packages...');

      setLoadingProgress(40);
      // Load base packages
      await py.loadPackage(PYODIDE_CONFIG.packages);

      // Install MangoGrid via micropip
      const micropip = py.pyimport('micropip');
      await micropip.install('MangoGrid');

      // Install additional packages if specified
      for (const pkg of additionalPackages) {
        addConsoleOutput('info', `ðŸ“¦ Installing ${pkg}...`);
        await micropip.install(pkg);
      }
      setLoadingProgress(80);

      // Set up output capture
      py.runPython(PYTHON_CAPTURE_SETUP);

      // Store reference and update state
      pyodideRef.current = py;
      setPyodide(py);
      updateStatus('ready', 'Ready');
      addConsoleOutput('success', 'âœ… MangoGrid ready!');
      setLoadingProgress(100);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(error instanceof Error ? error : new Error(errorMessage));
      updateStatus('error', 'Failed to load');
      addConsoleOutput('error', `âŒ ${errorMessage}`);
      console.error('Pyodide initialization error:', error);
    } finally {
      initializingRef.current = false;
    }
  }, [addConsoleOutput, updateStatus, additionalPackages]);

  // Run Python code (sync)
  const runPython = useCallback(
    async (code: string): Promise<unknown> => {
      if (!pyodideRef.current) {
        throw new Error('Pyodide not initialized');
      }
      return pyodideRef.current.runPython(code);
    },
    []
  );

  // Run Python code (async)
  const runPythonAsync = useCallback(
    async (code: string): Promise<unknown> => {
      if (!pyodideRef.current) {
        throw new Error('Pyodide not initialized');
      }
      setIsRunning(true);
      try {
        const result = await pyodideRef.current.runPythonAsync(code);
        return result;
      } finally {
        setIsRunning(false);
      }
    },
    []
  );

  // Auto-initialize on mount
  useEffect(() => {
    if (autoInit) {
      initialize();
    }
  }, [autoInit, initialize]);

  return {
    pyodide,
    status,
    message,
    isRunning,
    consoleOutput,
    error,
    loadingProgress,
    initialize,
    runPython,
    runPythonAsync,
    addConsoleOutput,
    clearConsole,
    isReady: status === 'ready',
  };
}

// ============================================================
// POWER FLOW EXECUTION
// ============================================================

/**
 * Build Python code for MangoGrid network from nodes and connections
 */
export function buildMangoGridNetworkCode(
  nodes: Array<{
    id: string;
    type: string;
    name: string;
    x: number;
    y: number;
    properties: Record<string, unknown>;
  }>,
  connections: Array<{
    id: string;
    type: string;
    from: string;
    to: string;
    properties: Record<string, unknown>;
  }>
): string {
  const escapePyString = (value: string) =>
    value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\r/g, '\\r').replace(/\n/g, '\\n');

  const safeName = (value: string) => escapePyString(value);

  // Helper to find nearest bus
  const findNearestBus = (node: { x: number; y: number }) => {
    const buses = nodes.filter((n) => n.type === 'bus');
    if (buses.length === 0) return null;
    return buses.reduce((a, b) =>
      Math.hypot(node.x - a.x, node.y - a.y) <
      Math.hypot(node.x - b.x, node.y - b.y)
        ? a
        : b
    );
  };

  let code = `
import MangoGrid
import json

n = MangoGrid.Network()
n.set_snapshots([0])
`;

  // NOTE: Node/connection names are interpolated into Python string literals below.
  // If names can contain quotes, backslashes, or newlines, they must be escaped or
  // sanitized first to avoid syntax errors or unintended code injection in Pyodide.
  // Add buses
  nodes
    .filter((n) => n.type === 'bus')
    .forEach((b) => {
      code += `n.add("Bus", "${safeName(b.name)}", v_nom=${b.properties.v_nom || 380})\n`;
    });

  // Add generators
  nodes
    .filter((n) => n.type === 'generator')
    .forEach((g) => {
      const bus = findNearestBus(g);
      if (bus) {
        code += `n.add("Generator", "${safeName(g.name)}", bus="${safeName(bus.name)}", p_nom=${g.properties.p_nom || 100}, marginal_cost=${g.properties.marginal_cost || 0})\n`;
      }
    });

  // Add loads
  nodes
    .filter((n) => n.type === 'load')
    .forEach((l) => {
      const bus = findNearestBus(l);
      if (bus) {
        code += `n.add("Load", "${safeName(l.name)}", bus="${safeName(bus.name)}", p_set=${l.properties.p_set || 50})\n`;
      }
    });

  // Add storage units
  nodes
    .filter((n) => n.type === 'storage')
    .forEach((s) => {
      const bus = findNearestBus(s);
      if (bus) {
        code += `n.add("StorageUnit", "${safeName(s.name)}", bus="${safeName(bus.name)}", p_nom=${s.properties.p_nom || 50}, max_hours=${s.properties.max_hours || 4})\n`;
      }
    });

  // Add lines
  connections
    .filter((c) => c.type === 'line')
    .forEach((line) => {
      const fromNode = nodes.find((n) => n.id === line.from);
      const toNode = nodes.find((n) => n.id === line.to);
      if (fromNode && toNode) {
        code += `n.add("Line", "${safeName(line.id)}", bus0="${safeName(fromNode.name)}", bus1="${safeName(toNode.name)}", r=${line.properties.r || 0.01}, x=${line.properties.x || 0.1}, s_nom=${line.properties.s_nom || 100})\n`;
      }
    });

  return code;
}

/**
 * Build Python code for running power flow and extracting results
 */
export function buildPowerFlowCode(): string {
  return `
try:
    n.pf()
    ok = True
    err = None
except Exception as e:
    ok = False
    err = str(e)

res = {'success': ok, 'error': err, 'buses': {}, 'lines': {}, 'summary': {}}
if ok:
    for b in n.buses.index:
        res['buses'][b] = {
            'v_mag_pu': float(n.buses_t.v_mag_pu[b].iloc[0]) if b in n.buses_t.v_mag_pu.columns else 1.0
        }
    for l in n.lines.index:
        res['lines'][l] = {
            'loading': abs(float(n.lines_t.p0[l].iloc[0]) / n.lines.loc[l, 's_nom'] * 100) if l in n.lines_t.p0.columns else 0
        }
    if res['buses']:
        res['summary'] = {
            'min_v': min(res['buses'][b]['v_mag_pu'] for b in res['buses']),
            'max_v': max(res['buses'][b]['v_mag_pu'] for b in res['buses']),
            'max_load': max((res['lines'][l]['loading'] for l in res['lines']), default=0)
        }
results_json = json.dumps(res)
results_json_size = len(results_json)
results_summary_json = json.dumps({
  'success': ok,
  'error': err,
  'summary': res.get('summary', {})
})
`;
}

/**
 * Parse power flow results from Pyodide
 */
export function parsePowerFlowResults(
  pyodide: PyodideInterface
): PowerFlowResults {
  const maxBytes = 2_000_000;
  let size = 0;
  try {
    const sizeValue = pyodide.globals.get('results_json_size');
    size = typeof sizeValue === 'number' ? sizeValue : 0;
  } catch {
    size = 0;
  }

  if (size > maxBytes) {
    let summary: PowerFlowResults['summary'] = {
      min_v: 1,
      max_v: 1,
      max_load: 0,
    };
    try {
      const summaryJson = pyodide.globals.get('results_summary_json');
      const parsed = JSON.parse(summaryJson);
      if (parsed?.summary) summary = parsed.summary;
    } catch {
      // ignore summary errors
    }

    return {
      success: false,
      error: `Results too large (${size} bytes). Enable backend execution or export results instead.`,
      buses: {},
      lines: {},
      summary,
    };
  }

  const resultsJson = pyodide.globals.get('results_json');
  return JSON.parse(resultsJson);
}

/**
 * Hook for running power flow analysis
 */
export function usePowerFlow(
  pyodide: UsePyodideReturn,
  onResults?: (results: PowerFlowResults) => void
) {
  const [results, setResults] = useState<PowerFlowResults | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const runPowerFlow = useCallback(
    async (
      nodes: Parameters<typeof buildMangoGridNetworkCode>[0],
      connections: Parameters<typeof buildMangoGridNetworkCode>[1]
    ): Promise<PowerFlowResults | null> => {
      if (!pyodide.isReady || !pyodide.pyodide) {
        pyodide.addConsoleOutput('error', 'Python not ready');
        return null;
      }

      const buses = nodes.filter((n) => n.type === 'bus');
      if (buses.length === 0) {
        pyodide.addConsoleOutput('error', 'Add at least one bus first');
        return null;
      }

      setIsRunning(true);
      pyodide.addConsoleOutput('info', '>>> Running power flow...');

      try {
        const networkCode = buildMangoGridNetworkCode(nodes, connections);
        const powerFlowCode = buildPowerFlowCode();
        const fullCode = networkCode + powerFlowCode;

        await pyodide.runPythonAsync(fullCode);
        const flowResults = parsePowerFlowResults(pyodide.pyodide);

        if (flowResults.success) {
          setResults(flowResults);
          onResults?.(flowResults);
          pyodide.addConsoleOutput('success', 'âœ… Power flow converged!');
          pyodide.addConsoleOutput(
            'info',
            `Voltage range: ${flowResults.summary?.min_v?.toFixed(3)} - ${flowResults.summary?.max_v?.toFixed(3)} pu`
          );
          pyodide.addConsoleOutput(
            'info',
            `Max line loading: ${flowResults.summary?.max_load?.toFixed(1)}%`
          );
        } else {
          pyodide.addConsoleOutput('error', `âŒ ${flowResults.error}`);
        }

        return flowResults;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        pyodide.addConsoleOutput('error', `âŒ ${errorMessage}`);
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [pyodide, onResults]
  );

  return {
    results,
    isRunning,
    runPowerFlow,
    clearResults: () => setResults(null),
  };
}

export default usePyodide;

